/**
 * Stripe Checkout client.
 *
 * Invokes the deployed `create-checkout-session` edge function which:
 *   1. Verifies the caller JWT
 *   2. Resolves the price ID from `tier_prices`
 *   3. Creates or reuses a Stripe Customer for auth.uid()
 *   4. Returns a Checkout Session URL
 *
 * Errors surface as `CheckoutNotConfiguredError` so the UI can show
 * a clear message instead of failing silently.
 */

import { supabase } from "@/integrations/supabase/client";
import type { TierId } from "@/lib/profile/types";
import { getTier } from "./tiers";

export type BillingInterval = "monthly" | "annual";

export interface CheckoutOpts {
  tierId: TierId;
  interval: BillingInterval;
  /** Where Stripe redirects after success. Defaults to /account?checkout=success. */
  successPath?: string;
  /** Where Stripe redirects on cancel. Defaults to /pricing?checkout=cancel. */
  cancelPath?: string;
  /** Optional promo / referral code. */
  promoCode?: string | null;
}

export class CheckoutNotConfiguredError extends Error {
  constructor(reason: string) {
    super(reason);
    this.name = "CheckoutNotConfiguredError";
  }
}

export const startCheckout = async (opts: CheckoutOpts): Promise<{ url: string }> => {
  const tier = getTier(opts.tierId);

  if (tier.id === "tier_signal") {
    throw new CheckoutNotConfiguredError("Signal is the free tier — no checkout required.");
  }

  const origin = window.location.origin;
  const { data, error } = await supabase.functions.invoke<{ url: string; error?: string }>(
    "create-checkout-session",
    {
      body: {
        tier_id: tier.id,
        interval: opts.interval,
        success_url: `${origin}${opts.successPath ?? "/account?checkout=success"}`,
        cancel_url: `${origin}${opts.cancelPath ?? "/pricing?checkout=cancel"}`,
        ...(opts.promoCode ? { promo_code: opts.promoCode } : {}),
      },
    },
  );

  // supabase-js surfaces non-2xx responses as { error }, but the body is also
  // present on `data`. Prefer the explicit body error message.
  if (data && "error" in data && data.error) {
    throw new CheckoutNotConfiguredError(String(data.error));
  }
  if (error) throw new CheckoutNotConfiguredError(error.message);
  if (!data?.url) throw new CheckoutNotConfiguredError("Checkout session did not return a URL.");
  return { url: data.url };
};

/**
 * Open the Stripe Customer Portal for the current user.
 * Backed by deployed edge function `create-portal-session`.
 */
export const openCustomerPortal = async (returnPath = "/account"): Promise<{ url: string }> => {
  const { data, error } = await supabase.functions.invoke<{ url: string }>(
    "create-portal-session",
    { body: { return_url: `${window.location.origin}${returnPath}` } },
  );
  if (error) throw new Error(error.message);
  if (!data?.url) throw new Error("Portal session did not return a URL.");
  return { url: data.url };
};
